"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

type WebImportProps = {
  onImport: (url: string) => void | Promise<void>;
  isLoading?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  align?: "start" | "center" | "end";
};

export function WebImport({
  onImport,
  isLoading,
  open: openProp,
  onOpenChange,
  children,
  className,
  align = "start",
}: WebImportProps) {
  const [url, setUrl] = useState("");
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
    if (!next) setUrl("");
  };

  const submit = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    const normalized = trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
    try {
      new URL(normalized);
    } catch {
      return;
    }
    try {
      await onImport(normalized);
      setOpen(false);
    } catch {
      /* keep popover open on error so the user can retry */
    }
  };

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(v) => {
        if (isLoading) return;
        setOpen(v);
      }}
    >
      <PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align={align}
          sideOffset={8}
          collisionPadding={16}
          data-portal-dropdown
          asChild
        >
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className={cn(
              "z-[56] flex h-8 w-[min(320px,calc(100vw-2rem))] min-w-0 items-center rounded-md border border-primary/35 bg-surface-elevated px-3 shadow-[0px_4px_20px_-4px_color-mix(in_srgb,var(--primary)_16%,transparent)] sm:w-auto sm:min-w-[320px]",
              className,
            )}
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <Icon
                name={isLoading ? "IconLoadingCircle" : "IconGlobe"}
                size={16}
                className={cn(
                  "shrink-0 text-primary",
                  isLoading && "animate-spin",
                )}
              />
              <Input
                type="text"
                variant="ghost"
                size="none"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void submit();
                  }
                }}
                placeholder="Enter a link to a position to import"
                autoFocus
                disabled={isLoading}
                className="min-w-0 flex-1 text-xs text-heading placeholder:text-contrast/40 disabled:opacity-60 lg:text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={isLoading || !url.trim()}
              aria-label="Import from link"
              className={cn(
                "ml-2 shrink-0 cursor-pointer transition-colors disabled:cursor-not-allowed",
                url.trim() && !isLoading
                  ? "text-primary hover:text-primary/80"
                  : "text-contrast/30",
              )}
            >
              <Icon name="IconArrowRight" size={16} />
            </button>
          </motion.div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export const WebImportButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function WebImportButton({ onClick, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      className={cn(
        "flex shrink-0 cursor-pointer items-center gap-0.5 transition-colors hover:opacity-80",
        className,
      )}
      {...props}
    >
      <Icon name="IconGlobe" size={16} className="text-primary dark:text-[#437DFC]" />
      <span className="px-0.5 text-sm font-medium text-primary dark:text-[#437DFC]">Web Import</span>
    </button>
  );
});
